import clsx from 'clsx';
import { useId, type ReactNode, type SelectHTMLAttributes, type InputHTMLAttributes } from 'react';

/* -------------------------------------------------------------------------
   Field

   Label, control, description and error, wired together by id.

   The wiring is the reason this exists rather than each form repeating it. A
   label that is not `htmlFor`-linked is a label a screen reader never announces,
   and an error message that is not referenced by `aria-describedby` is one a
   screen-reader user never hears — both are invisible in a visual review, which
   is exactly why they are so common.
   ------------------------------------------------------------------------- */

export interface FieldProps {
  label: string;
  /** Guidance shown under the control. Always announced with the field. */
  description?: string;
  /** Validation failure. Announced, and marks the control invalid. */
  error?: string | undefined;
  required?: boolean;
  children: (props: {
    id: string;
    'aria-describedby': string | undefined;
    'aria-invalid': boolean | undefined;
    required: boolean;
  }) => ReactNode;
}

export function Field({ label, description, error, required = false, children }: FieldProps) {
  const id = useId();
  const descriptionId = description ? `${id}-description` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [descriptionId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[12.5px] font-medium text-[var(--vc-text)]">
        {label}
        {required ? (
          <span className="ml-1 text-[var(--vc-danger-text)]" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>

      {children({
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
        required,
      })}

      {description ? (
        <p id={descriptionId} className="text-[12px] text-[var(--vc-text-subtle)]">
          {description}
        </p>
      ) : null}

      {error ? (
        <p id={errorId} className="text-[12px] text-[var(--vc-danger-text)]" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

const CONTROL_BASE =
  'h-9 w-full rounded-[var(--vc-radius-md)] border border-[var(--vc-border-strong)] ' +
  'bg-[var(--vc-surface)] px-2.5 text-[13px] text-[var(--vc-text)] ' +
  'transition-colors duration-[var(--vc-duration-fast)] ' +
  'placeholder:text-[var(--vc-text-subtle)] ' +
  'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--vc-focus-ring)] ' +
  'disabled:cursor-not-allowed disabled:opacity-60 ' +
  'aria-[invalid=true]:border-[var(--vc-danger-text)]';

export function TextInput({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={clsx(CONTROL_BASE, className)} {...rest} />;
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  // A native select, deliberately. It is keyboard-accessible everywhere, it uses
  // the platform's own picker on mobile, and no custom listbox reaches the same
  // level of correctness for a control this ordinary.
  return (
    <select className={clsx(CONTROL_BASE, 'pr-8', className)} {...rest}>
      {children}
    </select>
  );
}

/* -------------------------------------------------------------------------
   FormSection

   A labelled group inside a dialog form.

   A dialog that asks about two different things — what a tier costs, and what a
   licence issued under it carries — reads as one undifferentiated list without
   this, and an operator reasonably concludes the fields relate. They do not: a
   monthly price says nothing about a licence's validity.

   The heading is real, not decorative: the group is named for assistive
   technology through `aria-labelledby`, so the fields are announced as belonging
   to something.
   ------------------------------------------------------------------------- */

export function FormSection({ title, children }: { title: string; children: ReactNode }) {
  const headingId = useId();

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-4">
      <h3
        id={headingId}
        className="text-[11px] font-medium tracking-[0.06em] text-[var(--vc-text-subtle)] uppercase"
      >
        {title}
      </h3>
      {children}
    </section>
  );
}
